import axios from 'axios';
import { TicketListItem, TicketDetail, ReanalysisRequest, IngestResponse, HealthResponse, ProcessContext, PiiIgnoreEntry, ActiveDistribution, ScaffoldSegment } from '../types';
import { getAuthHeader, onAuthRejected, beginInteractiveLogin } from '../auth/identity';
import { formatBackendError } from '../utils/formatError';
import { readCachedVocabConfig, writeConfigCache, type CachedVocabConfig } from './configCache';
export type { CachedVocabConfig } from './configCache';
// Re-export so existing call sites can keep importing from services/api.
// Function bodies live in utils/formatError.ts so test files that auto-mock
// '../services/api' don't accidentally null them out.
export { formatBackendError, mapBackendError } from '../utils/formatError';

// <purpose>All API calls to the smarttix.core Function App (per-customer Bicep deployment).</purpose>
// <dependencies>getAuthHeader (auth/identity.ts) — Authorization: Bearer for cross-origin calls.</dependencies>
//
// Local dev: CRA proxy (setupProxy.js) routes /api → localhost:7071 (same-origin, no CORS).
// Production: REACT_APP_CORE_API_URL set at build time to the customer's FA (cross-origin).
const CORE_API_URL = process.env.REACT_APP_CORE_API_URL || '/api';

const coreHttp = axios.create({ baseURL: CORE_API_URL });
coreHttp.interceptors.request.use(async (config) => {
  const auth = await getAuthHeader();
  if (auth) config.headers[auth.name] = auth.value;
  return config;
});

// Why: backend sends camelCase via WorkerOptions.Serializer, but this safety net
// catches any residual PascalCase keys from WriteStringAsync paths.
const toCamelCase = (s: string): string => s.charAt(0).toLowerCase() + s.slice(1);
// Why: skip keys that are domain identifiers (K0, K3A, reason codes like beschwerde) — only camelize PascalCase property names
const shouldCamelize = (s: string): boolean => s.length > 1 && s.charAt(0) >= 'A' && s.charAt(0) <= 'Z' && s.charAt(1) >= 'a' && s.charAt(1) <= 'z';
const camelizeKeys = (obj: unknown): unknown => {
  if (Array.isArray(obj)) return obj.map(camelizeKeys);
  if (obj !== null && typeof obj === 'object' && !(obj instanceof Date) && !(obj instanceof Blob)) {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>).map(([k, v]) => [shouldCamelize(k) ? toCamelCase(k) : k, camelizeKeys(v)])
    );
  }
  return obj;
};
coreHttp.interceptors.response.use((response) => {
  if (response.config.responseType === 'blob') return response;
  if (response.data && typeof response.data === 'object') {
    response.data = camelizeKeys(response.data);
  }
  return response;
}, (error) => {
  // Why: a server-rejected token → backend 401 → empty dashboard instead of a login prompt.
  // Invalidate the token cache, then begin interactive login THROUGH the facade — it preserves
  // the current URL as the post-login return and owns the one door. Never a hard-coded href
  // here: a login link that bypasses the facade never feeds the MSAL cache.
  if (axios.isAxiosError(error) && error.response?.status === 401) {
    onAuthRejected();
    void beginInteractiveLogin();
  }
  return Promise.reject(error);
});


export interface UserRoles {
  email: string;
  isNormalUser: boolean;
  isCustomerAdmin: boolean;
  isAdmin: boolean;
}

export interface UserEntry {
  id: number;
  email: string;
  isNormalUser: boolean;
  isCustomerAdmin: boolean;
  isAdmin: boolean;
}

export interface RejectionFlowItem {
  fromPrimaryAxis: string;
  toPrimaryAxis: string;
  count: number;
}

export interface DailyTicketCount {
  ticketDate: string;
  count: number;
}

// Phase 4B rename (spec §6 naming table): class renamed outright (not itself wire — only its
// `.primaryAxis` property rides the wire, dual-served with the pre-rename `.category` shim during
// the expand window; see normalizePrimaryAxisVolumeBin below).
export interface PrimaryAxisVolumeBin {
  binLabel: string;
  binStart: string;
  primaryAxis: string;
  count: number;
}

// Per-bucket reanalysis retry distribution (TicketStatisticsDto.RetryBuckets, camelCased).
export interface PrimaryAxisRetryBuckets {
  zero: number;
  one: number;
  two: number;
  three: number;
  fourPlus: number;
}

export interface TicketStatistics {
  totalReceived: number;
  totalUnprocessed: number;
  totalOffen: number;
  totalApproved: number;
  totalCopyApproved?: number;
  totalArchived: number;
  archivedUnprocessed: number;
  archivedOffen: number;
  archivedApproved: number;
  avgProcessingSeconds: number | null;
  perPrimaryAxisCount: Record<string, number>;
  perSecondaryAxisCount: Record<string, number>;
  rejectionFlow: RejectionFlowItem[];
  dailyVolume: DailyTicketCount[];
  // Phase 4+: aggregated volume bins (kw/q). Present when aggregation != 'daily'.
  perPrimaryAxisVolumeByPeriod?: PrimaryAxisVolumeBin[];
  // Phase 5+: additional KPI fields from BE v2 response.
  reanalyseVerlauf?: Record<string, { total: number; approved: number }>;
  perPrimaryAxisA0Rate?: number | null;
  perPrimaryAxisA2Rate?: number | null;
  perPrimaryAxisOffen?: Record<string, number>;
  perPrimaryAxisErfolgreich?: Record<string, number>;
  // These four fields have no FE reader today (server-mirrored, contraction-ready wire fields —
  // served now so a future stats surface can consume them without a BE round-trip; not dead code,
  // just unbuilt UI).
  perPrimaryAxisRetryBuckets?: Record<string, PrimaryAxisRetryBuckets>;
  // Per-axis BASELINE_K* values loaded from customer_config. Used by the PDF export's
  // Abnahmekriterien column.
  perPrimaryAxisBaselines?: Record<string, number>;
  avgClassifierConfidence?: number | null;
  highClassifierConfidenceRate?: number | null;
  archivedFehlgeschlagenCount?: number;
  totalFehlgeschlagenCount?: number;
  archivedUnclassifiedCount?: number;
  totalUnclassifiedCount?: number;
  headlineRateMode?: string;
  requestedAggregation?: string;
}

// ═══════════════════════════════════════════════════════════
// Phase 4B expand-window boundary coalesce (spec §6, task F1)
// ═══════════════════════════════════════════════════════════
// Expand-window tolerance, the FE's only knowledge of the legacy wire names: a pre-expansion BE
// serves only the old pair, an expanded BE serves both — coalesce once here at the parse
// boundary, deleted at the contraction (see smarttix-core.md § Contraction discipline). No
// component below this line may mention the legacy names.

const normalizeTicket = <T extends {
  primaryAxis?: string; category?: string;
  classifierConfidence?: number; categoryConfidence?: number;
  secondaryAxis?: string[]; reasonCodes?: string[];
}>(raw: T): T => ({
  ...raw,
  primaryAxis: raw.primaryAxis ?? raw.category,
  classifierConfidence: raw.classifierConfidence ?? raw.categoryConfidence,
  secondaryAxis: raw.secondaryAxis ?? raw.reasonCodes,
});

const normalizeSuggestion = <T extends {
  primaryAxis?: string; category?: string;
  suggestionConfidence?: number; confidence?: number;
}>(raw: T): T => ({
  ...raw,
  primaryAxis: raw.primaryAxis ?? raw.category,
  suggestionConfidence: raw.suggestionConfidence ?? raw.confidence,
} as T);

const normalizeRejectionFlowItem = <T extends {
  fromPrimaryAxis?: string; fromCategory?: string;
  toPrimaryAxis?: string; toCategory?: string;
}>(raw: T): T => ({
  ...raw,
  fromPrimaryAxis: raw.fromPrimaryAxis ?? raw.fromCategory,
  toPrimaryAxis: raw.toPrimaryAxis ?? raw.toCategory,
} as T);

const normalizePrimaryAxisVolumeBin = <T extends { primaryAxis?: string; category?: string }>(raw: T): T => ({
  ...raw,
  primaryAxis: raw.primaryAxis ?? raw.category,
});

// Statistics carries the widest fan-out of renamed fields (spec §6 E3 list) — one coalesce home
// covering every sub-object the FE consumes, applied once at getStatistics' parse boundary.
const normalizeStatistics = (raw: any): TicketStatistics => ({
  ...raw,
  perPrimaryAxisCount: raw.perPrimaryAxisCount ?? raw.categories,
  perSecondaryAxisCount: raw.perSecondaryAxisCount ?? raw.reasonCodes,
  rejectionFlow: raw.rejectionFlow?.map(normalizeRejectionFlowItem),
  perPrimaryAxisVolumeByPeriod: (raw.perPrimaryAxisVolumeByPeriod ?? raw.categoryVolumeByPeriod)?.map(normalizePrimaryAxisVolumeBin),
  perPrimaryAxisA0Rate: raw.perPrimaryAxisA0Rate ?? raw.categoryA0Rate,
  perPrimaryAxisA2Rate: raw.perPrimaryAxisA2Rate ?? raw.categoryA2Rate,
  perPrimaryAxisOffen: raw.perPrimaryAxisOffen ?? raw.categoryOffen,
  perPrimaryAxisErfolgreich: raw.perPrimaryAxisErfolgreich ?? raw.categoryErfolgreich,
  perPrimaryAxisRetryBuckets: raw.perPrimaryAxisRetryBuckets ?? raw.categoryRetryDistribution,
  perPrimaryAxisBaselines: raw.perPrimaryAxisBaselines ?? raw.categoryBaselines,
  avgClassifierConfidence: raw.avgClassifierConfidence ?? raw.avgCategoryConfidence,
  highClassifierConfidenceRate: raw.highClassifierConfidenceRate ?? raw.highConfidenceRate,
  archivedUnclassifiedCount: raw.archivedUnclassifiedCount ?? raw.archivedUnklarCount,
  totalUnclassifiedCount: raw.totalUnclassifiedCount ?? raw.totalUnklarCount,
});

export interface UiWarps {
  approveToGenehmigt: boolean;
  processToAntworten: boolean;
  testgenProcessToAntworten: boolean;
  testgenUploadToUploads: boolean;
}

// One value on a classification axis. `id`/`label` are the two the wire has always carried; the
// four optional fields below mirror core's `SchemaValuePayload` EXACTLY, including its
// omit-when-null serialization — a value that carries none of them arrives with none of the keys,
// which is what makes them additive: a backend predating them, and every axis that has no use for
// them (wim's handling axis carries none), deserialize identically to before.
// - abbr: the value's own COMPACT SPELLING, for the surfaces that render a code rather than a word
//   (the colour strip, the legend, the second-axis pill). Served rather than derived: this
//   frontend used to truncate the id to three characters, which is not an
//   abbreviation anyone decided and cannot be re-worded without a deploy. Read it through
//   useCategoryMeta().abbr — the tier order below it (DEFAULT_ABBR, then the served label) has one
//   home, in categoryMeta.resolveAbbr, and re-implementing it at a call site is how a role starts
//   rendering a spelling nobody chose.
// - minCategory/maxCategory: the primary-axis band this SECOND-axis value is valid for (the served
//   form of the baked REASON_CODES min/max pair). Consumed from Task 9 onward; typed here because
//   the wire already carries them and a field the FE cannot see is a field the FE cannot honour.
// - excludedFromEvaluation: THE marker. The tenant declaring "this value is the unclassified bucket, not a
//   classification outcome" — the served replacement for the frontend knowing that gMSB's unclassified id is
//   spelled 'KU'. Read it via useCategoryMeta().performanceCategoryKeys, never by re-filtering
//   here: the split between reconciliation and categorization-performance enumeration has exactly
//   one home (ClassificationSchemaContext).
export interface SchemaValue {
  id: string;
  label: string;
  abbr?: string;
  minCategory?: string;
  maxCategory?: string;
  excludedFromEvaluation?: boolean;
}
export interface VnbSlot { id: string; label: string; source: 'mail' | 'system' | 'hybrid'; }
export interface ClassificationSchema { marktprozess: SchemaValue[]; anliegen: SchemaValue[]; slots: VnbSlot[]; }

// One classification axis as DATA: the schema's own axis id, the German UI noun for it, and its
// values. This is what lets the FE render categories without knowing a role name — a new
// schema-driven role needs no frontend change at all.
// nounPlural/nounCompound are OPTIONAL and absent whenever the base noun already reads correctly
// in that position — the frontend defaults them to `noun`, so an axis that needs no special form
// (and a backend that predates these fields) behaves exactly as it did before they existed.
export interface VocabularyAxis {
  axis: string;
  noun: string;
  nounPlural?: string;
  nounCompound?: string;
  values: SchemaValue[];
}

// Emitted ALONGSIDE classificationSchema, never replacing it: a bundle that predates this field
// keeps working unchanged. Null for roles with no active schema (gMSB/wMSB → legacy K-path).
export interface ClassificationVocabulary { primary: VocabularyAxis; secondary: VocabularyAxis | null; }

export interface AppConfig {
  chatbotEnabled: boolean;
  chatbotBaseUrl: string;
  // null ONLY on a failed /config (the catch path below) — never a fabricated role. The BE always
  // sends a role string on success; whether that string is a KNOWN role is App.tsx's call
  // (isKnownRole), not this layer's — a drifted/unrecognised string still arrives here verbatim.
  role: string | null;
  uiWarps: UiWarps;
  autoArchiveDays: number;
  classificationSchema?: ClassificationSchema | null;
  classificationVocabulary?: ClassificationVocabulary | null;
  uiRowLayout?: 'strip_label' | 'strip_abbr';
  // Eingangsweg row visibility on TicketDetail (operator_config UI_SHOW_SOURCE_CHANNEL).
  // Optional on the wire so a bundle deployed against an older backend simply reads undefined.
  uiShowSourceChannel?: boolean;
  // Demo-only: gates the "Simulierte Systemdaten" button. Mirrors the endpoint's allowlist gate
  // (true only for enerviedemo/donetzdemo) so the button never shows where the endpoint would 404.
  simulateSystemData?: boolean;
  // Tenant-wide capability gate for the form-to-Excel affordance (both list pages). Fail-closed:
  // absent on an older backend or on any /config failure means the tenant does not own the
  // capability, so the entry button never renders — distinct from the per-bucket
  // `counts.formExtractionEnabled` a ticket-list response carries, which only explains a refusal
  // once the operator is already inside excel-selection mode.
  formExtractionEnabled?: boolean;
  // Whether inline images count as attachments (operator_config UI_ATTACHMENTS_COUNT_INLINE).
  // ⚠ Absent ⇒ TRUE, the inverse of uiShowSourceChannel above: absence means "nobody has asked
  // for a change", and the unchanged behaviour is counting them.
  uiAttachmentsCountInline?: boolean;
}

// Mirror of smarttix.core brand_config row (GET /brand). Colours null = auto; all set = exact override.
export interface BrandConfig {
  primaryColor: string | null;
  primaryHover: string | null;
  accentColor: string | null;
  accentHover: string | null;
  bg: string | null;
  bgSecondary: string | null;
  textColor: string | null;
  textMuted: string | null;
  border: string | null;
  font: string | null;
  textSize: string | null;
  titleSize: string | null;
  fontWeight: number | null;
  title: string;
  greeting: string;
  showBranding: boolean;
}

// Why: server-default fallback matches migration 037 defaults so a /config 4xx
// (e.g. ancient backend without uiWarps) doesn't flip historical warps off mid-deploy.
const DEFAULT_UI_WARPS: UiWarps = {
  approveToGenehmigt: false,
  processToAntworten: true,
  testgenProcessToAntworten: true,
  testgenUploadToUploads: true,
};

// THE fetch seam (Task C, layer b): every successful resolution writes its vocabulary/role/
// row-layout slice into the sessionStorage cache (configCache.ts) — App.tsx reads that cache
// through once at boot for an instant first paint. This function ALWAYS still hits the network on
// every call, cache or no cache: EinstellungenSeite's explicit refreshConfig() after a settings
// save (and Task B's formExtractionEnabled security gate) depend on that never being skipped — the
// cache only ever shortens the pre-paint window, never substitutes for the authoritative fetch. A
// FAILED fetch (catch path below) deliberately does NOT touch the cache: a transient outage must
// never overwrite a good cached entry with nothing.
export const getConfig = async (): Promise<AppConfig> => {
  try {
    const response = await coreHttp.get<AppConfig>('/config');
    const normalized: AppConfig = {
      ...response.data,
      uiWarps: { ...DEFAULT_UI_WARPS, ...(response.data.uiWarps ?? {}) },
      autoArchiveDays: response.data.autoArchiveDays ?? 0,
      classificationSchema: response.data.classificationSchema ?? null,
      // Absent on an older backend → null → the FE behaves exactly as it did before this field existed.
      classificationVocabulary: response.data.classificationVocabulary ?? null,
      uiRowLayout: response.data.uiRowLayout ?? undefined, // null/unset → App applies the role-aware default
      simulateSystemData: response.data.simulateSystemData ?? false,
      // ⚠ Absent ⇒ HIDDEN, in both the success and the catch path below. A backend that does not
      // know the field yet, and a /config that failed outright, mean the same thing here: nobody
      // asked for the row. Defaulting it visible would ship the state the operator's E2E rejected.
      uiShowSourceChannel: response.data.uiShowSourceChannel ?? false,
      // Same fail-closed shape: absent means the capability is not owned, never a guessed grant.
      formExtractionEnabled: response.data.formExtractionEnabled ?? false,
      // ⚠ Absent ⇒ TRUE — inverted from every other flag in this block on purpose. This is a
      // display preference, not a capability: nobody having set it means "keep counting inline
      // images", which is the behaviour every already-live tenant has today. A `false` here would
      // silently change what clerks see, so it takes the opposite fallback direction from
      // uiShowSourceChannel/formExtractionEnabled above.
      uiAttachmentsCountInline: response.data.uiAttachmentsCountInline ?? true,
    };
    writeConfigCache({
      role: normalized.role,
      classificationSchema: normalized.classificationSchema ?? null,
      classificationVocabulary: normalized.classificationVocabulary ?? null,
      uiRowLayout: normalized.uiRowLayout,
      uiAttachmentsCountInline: normalized.uiAttachmentsCountInline,
    });
    return normalized;
  } catch {
    // Why role: null (not a fabricated 'gmsb'): a failed /config means the FE genuinely does not
    // know the tenant's role. Silently assuming gmsb dressed a backend outage as a working gmsb
    // tenant — the caller (App.tsx) maps this to the neutral/warning UI instead.
    // uiAttachmentsCountInline: true even here — a backend outage must not look like a tenant
    // asking to stop counting inline images; see the success-path comment above for why this one
    // flag inverts direction from its fail-closed neighbours in this same object.
    return { chatbotEnabled: false, chatbotBaseUrl: '', role: null, uiWarps: DEFAULT_UI_WARPS, autoArchiveDays: 0, classificationSchema: null, classificationVocabulary: null, uiRowLayout: undefined, simulateSystemData: false, uiShowSourceChannel: false, formExtractionEnabled: false, uiAttachmentsCountInline: true };
  }
};

// Read-through for App.tsx's instant first paint (Task C) — see configCache.ts for the full
// contract (self-consistency check, session-scoping rationale, why capability flags never ride
// along). Never touches the network; a miss (first-ever boot this tab) simply returns null and
// the caller keeps its existing neutral defaults until getConfig() resolves.
export const getCachedVocabConfig = (): CachedVocabConfig | null => readCachedVocabConfig();

// Fire-and-forget /config pre-warm (Task C, layer c) — App.tsx fires this once when the Anmeldung
// (login) screen renders, before the operator has necessarily completed sign-in. Deliberately
// bypasses coreHttp: its response interceptor above begins interactive login on ANY 401, and
// firing that from the login screen itself — before the operator has clicked anything — would be
// an unsolicited navigation on the one page whose entire job is to wait for their click. A bare
// fetch cannot trigger that interceptor. The expected pre-login 401 is handled by `r.ok` below,
// NOT by the trailing .catch(): fetch() only rejects on a genuine network-level failure (offline,
// DNS, CORS) — an HTTP error STATUS like 401 is still a resolved response, just with `ok: false`,
// so it flows into the `r.ok ? r.json() : null` branch and resolves to `null`, which the `if
// (data && …)` guard below then skips silently. .catch() exists only for that separate,
// network-level failure class.
// Returns void, never a promise the caller is expected to await or chain — this exists purely to
// warm the backend's cold start / DB connection pool before the REAL getConfig() call fires
// post-login. Feeding the vocabulary cache from a genuinely-successful response (e.g. an
// already-valid SWA session cookie riding along on this tab) is a bonus, not the point — it
// deliberately skips getConfig()'s own normalization (uiWarps merge etc.), since an
// under-normalized cache entry is corrected by the very next real getConfig() call regardless, and
// only the vocabulary-relevant slice is ever written (never formExtractionEnabled/uiWarps/etc. —
// see configCache.ts's own constraint against caching capability flags from an unauthenticated call).
export const prewarmConfig = (): void => {
  fetch(`${CORE_API_URL}/config`, { credentials: 'include' })
    .then(r => (r.ok ? r.json() : null))
    .then((data: Partial<AppConfig> | null) => {
      if (data && typeof data === 'object' && 'role' in data) {
        writeConfigCache({
          role: data.role ?? null,
          classificationSchema: data.classificationSchema ?? null,
          classificationVocabulary: data.classificationVocabulary ?? null,
          uiRowLayout: data.uiRowLayout,
        });
      }
    })
    .catch(() => { /* silent — warm-up only, see purpose above */ });
};

// GET /brand — the customer's stored brand row (Chatbot-Testseite preview). Null on any error.
export const getBrand = async (): Promise<BrandConfig | null> => {
  try {
    const response = await coreHttp.get<BrandConfig>('/brand');
    return response.data;
  } catch (err) {
    console.error('[API] Failed to get brand', err);
    return null;
  }
};

// Mirror of smarttix.core brand_detected_cache (GET /brand/detected) — the auto-detected+corrected
// palette the live widget last rendered. All colours concrete; 204/absent → null (never detected yet).
export interface DetectedBrand {
  primaryColor: string;
  primaryHover: string;
  accentColor: string;
  accentHover: string;
  bg: string;
  bgSecondary: string;
  textColor: string;
  textMuted: string;
  border: string;
  font: string;
  textSize: string | null;
  titleSize: string | null;
  fontWeight: number | null;
  detectedAt: string;
}

// GET /brand/detected — the last auto-detected palette for the Chatbot-Testseite. Null on 204
// (zero-visit), empty body, or any error. The auto card renders it inline when present.
export const getBrandDetected = async (): Promise<DetectedBrand | null> => {
  try {
    const response = await coreHttp.get<DetectedBrand | ''>('/brand/detected');
    // 204 No Content → axios yields status 204 with '' data; a real row → the object.
    if (response.status === 204 || !response.data) return null;
    return response.data as DetectedBrand;
  } catch (err) {
    console.error('[API] Failed to get detected brand', err);
    return null;
  }
};

export const getUserRoles = async (email?: string): Promise<UserRoles | null> => {
  try {
    const url = email ? `/roles?email=${encodeURIComponent(email)}` : `/roles`;
    console.log(`[API] Fetching user roles for: ${email || 'current user'} from ${url}`);
    const response = await coreHttp.get<UserRoles>(url);
    console.log('[API] User roles received:', response.data);
    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      console.error(`[API] Failed to get user roles: ${err.response?.status} ${err.response?.statusText}`, err.response?.data);
    } else {
      console.error('[API] Failed to get user roles (non-axios error)', err);
    }
    return null;
  }
};

function serializeDateRanges(ranges: Array<{start: Date; end: Date}>): string {
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return ranges.map(r => `dateRanges=${fmt(r.start)}..${fmt(r.end)}`).join('&');
}

export const getStatistics = async (
  dateRanges: Array<{start: Date; end: Date}>,
  dateField?: string,
  aggregation?: string,
): Promise<TicketStatistics | null> => {
  try {
    const parts: string[] = [];
    if (dateRanges.length > 0) parts.push(serializeDateRanges(dateRanges));
    if (dateField) parts.push(`dateField=${encodeURIComponent(dateField)}`);
    if (aggregation && aggregation !== 'daily') parts.push(`aggregation=${encodeURIComponent(aggregation)}`);
    const url = parts.length > 0 ? `/statistics?${parts.join('&')}` : '/statistics';
    const response = await coreHttp.get<TicketStatistics>(url);
    return normalizeStatistics(response.data);
  } catch (err) {
    console.error('Failed to get statistics', err);
    return null;
  }
};

export const getActiveDistribution = async (): Promise<ActiveDistribution | null> => {
  try {
    const response = await coreHttp.get<ActiveDistribution>(`/tickets/active/distribution`);
    return response.data;
  } catch (err) {
    console.error('Failed to get active distribution', err);
    return null;
  }
};

export const getMonthlyTicketCounts = async (dateField: string): Promise<Record<string, number>> => {
  try {
    const url = `/statistics/monthly-counts?dateField=${encodeURIComponent(dateField)}`;
    const response = await coreHttp.get<Record<string, number>>(url);
    return response.data;
  } catch (err) {
    console.error('Failed to fetch monthly counts', err);
    return {};
  }
};

/** Trigger browser file download from a Blob. */
const downloadBlob = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const exportStatistics = async (
  dateRanges: Array<{start: Date; end: Date}>,
  dateField: string,
  format: 'xlsx' | 'pdf',
  aggregation?: string,
  // WCAG 2.2.1 / DGUV Ch.9 — cancellable long op. BE may still finish rendering
  // the export after cancel; the FE just discards the response. Acceptable trade-off
  // (no client-state corruption; BE work is read-only).
  options?: { signal?: AbortSignal },
): Promise<void> => {
  const parts: string[] = [`dateField=${encodeURIComponent(dateField)}`, `format=${encodeURIComponent(format)}`];
  if (dateRanges.length > 0) parts.push(serializeDateRanges(dateRanges));
  if (aggregation && aggregation !== 'daily') parts.push(`aggregation=${encodeURIComponent(aggregation)}`);
  const response = await coreHttp.get(`/statistics/export?${parts.join('&')}`, {
    responseType: 'blob',
    signal: options?.signal,
  });
  const ext = format === 'pdf' ? 'pdf' : 'xlsx';
  const firstStart = dateRanges[0]?.start;
  const lastEnd = dateRanges[dateRanges.length - 1]?.end;
  const fromStr = firstStart ? firstStart.toISOString().slice(0, 10).replace(/-/g, '') : 'unknown';
  const toStr = lastEnd ? lastEnd.toISOString().slice(0, 10).replace(/-/g, '') : 'unknown';
  // The filename's single home is the server's Content-Disposition (deterministic per tenant —
  // the client's downstream tooling refreshes from a fixed path). The date-range name survives
  // only as the fallback for a backend that does not expose the header cross-origin yet.
  const fallback = `statistik-${fromStr}-${toStr}.${ext}`;
  const filename = filenameFromContentDisposition(response.headers['content-disposition'], fallback);
  downloadBlob(new Blob([response.data]), filename);
};

// Parses `attachment; filename="..."` (quoted or bare). Exported for its test; consumed only by
// exportStatistics above. Never throws — an unparseable header is the fallback's case.
export const filenameFromContentDisposition = (header: string | null | undefined, fallback: string): string => {
  if (!header) return fallback;
  const match = /filename\*?=(?:UTF-8''|")?([^";]+)"?/i.exec(header);
  return match?.[1]?.trim() || fallback;
};



// ═══════════════════════════════════════════════════════════
// Smarttix Pipeline API (cross-origin, auth via interceptor)
// ═══════════════════════════════════════════════════════════

// <purpose>The download's visible filename.</purpose>
// <constraints>Mirrors FormBatchExcelBuilder.FileNameFor on the server. NOT "aufbauliste.xlsx" — that read
// as the customer's own official sheet, which is the opposite of what this file is, and repeat downloads
// collided into "(1)". Row count so two downloads are distinguishable at a glance; timestamp
// largest-unit-first so a folder sorts chronologically on name alone. Local time on purpose: the clerk
// reads it against their own clock, not the server's.</constraints>
const notizblattFileName = (rowCount: number): string => {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  const stamp = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}_${p(d.getHours())}${p(d.getMinutes())}`;
  return `Notizblatt_${rowCount}-Zeilen_${stamp}.xlsx`;
};

export const smarttix = {
  checkHealth: async (): Promise<HealthResponse> => {
    const response = await coreHttp.get<HealthResponse>('/tickets/health');
    return response.data;
  },

  ingestTicket: async (file: File, options?: { parseOnly?: boolean; context?: string; signal?: AbortSignal }): Promise<IngestResponse> => {
    const formData = new FormData();
    formData.append('file', file, file.name);
    if (options?.context) formData.append('context', options.context);
    // mig 072 — this client DECLARES its own transport route rather than letting the server infer one.
    // ⚠ It is a declaration, not proof: the server normalizes it against a closed vocabulary and stores
    // NULL for anything it does not recognise, and nothing downstream may treat it as a security fact.
    // The other route ('forward', a future Outlook POST flow) declares itself the same way when it is
    // built; until then those tickets are honestly unknown rather than defaulted into a claim.
    formData.append('channel', 'upload');
    const params = options?.parseOnly ? '?parseOnly=true' : '';
    const response = await coreHttp.post<IngestResponse>(
      `/tickets/ingest${params}`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        // WCAG 2.2.1 / DGUV Ch.9 — long ops must be cancellable. axios respects AbortSignal.
        signal: options?.signal,
      }
    );
    return response.data;
  },

  processTicket: async (uuid: string, processContext?: ProcessContext): Promise<{ success: boolean; error?: string }> => {
    try {
      const body: Record<string, unknown> = processContext && Object.keys(processContext).length > 0 ? { ...processContext } : {};
      // Phase 4B expand window: dual-send both the old and new axis-vocabulary body key with the
      // SAME value — an old (pre-expansion) BE reads forceCategory/forceReasonCodes, an expanded
      // one can assert the two agree. Canonical source is the new-named field (ProcessContext);
      // the old key is computed FROM it, never the other way — same direction as the query-param senders above.
      if (processContext?.forcePrimaryAxis !== undefined) body.forceCategory = processContext.forcePrimaryAxis;
      if (processContext?.forceSecondaryAxis !== undefined) body.forceReasonCodes = processContext.forceSecondaryAxis;
      await coreHttp.post(`/tickets/${uuid}/process`, body);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Verarbeiten') };
    }
  },

  deleteTicket: async (uuid: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await coreHttp.delete(`/tickets/${uuid}`);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Löschen') };
    }
  },

  bulkDeleteTickets: async () => {
    try {
      const response = await coreHttp.delete<{ deletedAt: string }>(`/tickets`);
      return response.data;
    } catch (error: any) {
      return { success: false as const, error: formatBackendError(error, 'Massenlöschen') };
    }
  },

  undoBulkDelete: async (deletedAt: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await coreHttp.post('/tickets/undo-bulk-delete', { deletedAt });
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Rückgängig machen') };
    }
  },

  getArchivedPage: async (
    p: {
      bucket: 'erledigt' | 'liegengeblieben' | 'ungeklaert';
      search?: string;
      primaryAxis?: string;
      secondaryAxis?: string[];
      from?: string | null;
      to?: string | null;
      uploadedBy?: string;
      cursor?: string | null;
      limit?: number;
    }
  ): Promise<{ rows: TicketListItem[]; nextCursor: string | null; counts: { erledigt: number; liegengeblieben: number; ungeklaert: number } | null }> => {
    const params = new URLSearchParams({ bucket: p.bucket });
    if (p.search) params.append('search', p.search);
    // Phase 4B expand window: dual-send both the old and new axis-vocabulary param name with the
    // SAME value — an old (pre-expansion) BE reads 'category'/'reasonCodes', an expanded one can
    // assert the two agree (see normalizeTicket above for the read-side coalesce).
    if (p.primaryAxis) { params.append('category', p.primaryAxis); params.append('primaryAxis', p.primaryAxis); }
    if (p.secondaryAxis && p.secondaryAxis.length > 0) {
      const v = p.secondaryAxis.join(',');
      params.append('reasonCodes', v);
      params.append('secondaryAxis', v);
    }
    if (p.from) params.append('from', p.from);
    if (p.to) params.append('to', p.to);
    if (p.uploadedBy) params.append('uploadedBy', p.uploadedBy);
    if (p.cursor) params.append('cursor', p.cursor);
    if (p.limit) params.append('limit', p.limit.toString());
    const response = await coreHttp.get<{ rows: TicketListItem[]; nextCursor: string | null; counts: { erledigt: number; liegengeblieben: number; ungeklaert: number } | null }>(`/tickets/archived?${params.toString()}`);
    return { ...response.data, rows: response.data.rows.map(normalizeTicket) };
  },

  getActivePage: async (
    p: {
      bucket: 'uploads' | 'offen' | 'genehmigt';
      sort: 'uploaded' | 'received' | 'updated' | 'rejections' | 'zone';
      dir?: 'desc' | 'asc';
      search?: string;
      primaryAxis?: string;
      secondaryAxis?: string[];
      from?: string | null;
      to?: string | null;
      uploadedBy?: string;
      cursor?: string | null;
      limit?: number;
    }
  ): Promise<{ rows: TicketListItem[]; nextCursor: string | null; counts: { uploads: number; offen: number; genehmigt: number; formBearing?: { uploads: number; offen: number; genehmigt: number }; formBatchCap?: number; formExtractionEnabled?: boolean } | null }> => {
    const params = new URLSearchParams({ bucket: p.bucket, sort: p.sort });
    if (p.dir) params.append('dir', p.dir);
    if (p.search) params.append('search', p.search);
    // Phase 4B expand window: dual-send both the old and new axis-vocabulary param name with the
    // SAME value — an old (pre-expansion) BE reads 'category'/'reasonCodes', an expanded one can
    // assert the two agree (see normalizeTicket above for the read-side coalesce).
    if (p.primaryAxis) { params.append('category', p.primaryAxis); params.append('primaryAxis', p.primaryAxis); }
    if (p.secondaryAxis && p.secondaryAxis.length > 0) {
      const v = p.secondaryAxis.join(',');
      params.append('reasonCodes', v);
      params.append('secondaryAxis', v);
    }
    if (p.from) params.append('from', p.from);
    if (p.to) params.append('to', p.to);
    if (p.uploadedBy) params.append('uploadedBy', p.uploadedBy);
    if (p.cursor) params.append('cursor', p.cursor);
    if (p.limit) params.append('limit', p.limit.toString());
    const response = await coreHttp.get<{ rows: TicketListItem[]; nextCursor: string | null; counts: { uploads: number; offen: number; genehmigt: number; formBearing?: { uploads: number; offen: number; genehmigt: number }; formBatchCap?: number; formExtractionEnabled?: boolean } | null }>(`/tickets/active?${params.toString()}`);
    return { ...response.data, rows: response.data.rows.map(normalizeTicket) };
  },

  getTicket: async (uuid: string): Promise<TicketDetail | null> => {
    try {
      const response = await coreHttp.get<any>(`/tickets/${uuid}`);
      const detail = normalizeTicket(response.data);
      return { ...detail, suggestion: detail.suggestion ? normalizeSuggestion(detail.suggestion) : detail.suggestion };
    } catch {
      return null;
    }
  },

  // Audit one PII pill reveal click. BE writes a smarttix_log row with type+ordinal — never the value.
  // Fire-and-forget from the FE perspective; failures are swallowed (audit is defense-in-depth, not a hard gate).
  logPiiReveal: async (uuid: string, fieldType: string, index: number): Promise<void> => {
    try {
      await coreHttp.post(`/tickets/${uuid}/pii-reveal-audit`, { fieldType, index });
    } catch { /* silent — see audit-as-defense-in-depth in pii-reveal-defensibility.md */ }
  },

  approveTicket: async (uuid: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await coreHttp.post(`/tickets/${uuid}/approve`);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Genehmigen') };
    }
  },

  // WIM assisted action (actor-bound confirm): suggestionVersion = the suggestion row id —
  // reanalysis inserts a new row, so a stale id is refused server-side (stale_rejected).
  confirmTriageAction: async (
    uuid: string, suggestionVersion: number, action: 'forward' | 'ack' | 'archive',
  ): Promise<{ success: boolean; resultState?: string; error?: string }> => {
    try {
      const res = await coreHttp.post<{ executed: boolean; resultState: string; rejectReason?: string }>(
        '/manage/wim/actions', { suggestionUuid: uuid, suggestionVersion, action });
      return res.data.executed
        ? { success: true, resultState: res.data.resultState }
        : { success: false, resultState: res.data.resultState, error: res.data.rejectReason };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Triage-Aktion') };
    }
  },

  // Copy = indirect approval (#copy-approval). Sets copy_approved_at WITHOUT
  // advancing status — the ticket stays in Offen. undoCopyApprove is the
  // write-then-revert undo (DELETE clears copy_approved_at).
  copyApproveTicket: async (uuid: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await coreHttp.post(`/tickets/${uuid}/copy-approve`);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Kopieren') };
    }
  },

  // Per-ticket counterpart of downloadFormRowsXlsx. The row is assembled SERVER-side and fetched on
  // click rather than shipped on the detail DTO — the DTO carries only hasFormExtraction, so no form
  // field values reach the browser until a clerk actually asks for the row.
  // The server may REFUSE. The clipboard carries values only, while the workbook carries the notes that
  // say what was read and what disagreed — and the destination sheet is terminal, so a questionable cell
  // copied by clipboard arrives with no way to check it. A refusal is a 200 with a reason, not an error,
  // and the caller renders that reason.
  copyFormRow: async (uuid: string): Promise<{ row?: string; blocked: boolean; reason?: string }> => {
    const res = await coreHttp.post<{ row?: string; blocked?: boolean; reason?: string }>(
      `/tickets/${uuid}/form-row`, {});
    return { row: res.data.row, blocked: res.data.blocked === true, reason: res.data.reason };
  },

  undoCopyApprove: async (uuid: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await coreHttp.delete(`/tickets/${uuid}/copy-approve`);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Rückgängig') };
    }
  },

  // CS-rep edit-before-approve (#5). Status must be suggestion_ready.
  // scaffoldSegments is additive (arc243 Part A Task 7): omitted for K-path/free_text (no
  // scaffold), so the PATCH body stays byte-identical to pre-Task-7 — present ⇒ the server
  // rewrites scaffold_json's segments node, so a confirmed Klärfall fill survives a reload.
  // suggestionId (fix-review round 1) rides ALONGSIDE scaffoldSegments, never alone — it is the
  // draft's own identity (ai_suggestions.id, the receiver's draftKey) so the server merges into
  // the EXACT row the fill was confirmed against, not whichever row is newest at commit time
  // (a stale client / a reanalysis race). The core handler 400s if scaffoldSegments arrives
  // without it — never falls back to "latest row" — so leaving it out here is not a safe omission.
  editSuggestion: async (
    uuid: string,
    editedText: string,
    scaffoldSegments?: ScaffoldSegment[],
    suggestionId?: number,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      await coreHttp.patch(`/tickets/${uuid}/suggestion-edit`, {
        editedText,
        ...(scaffoldSegments ? { scaffoldSegments, suggestionId } : {}),
      });
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Speichern der Bearbeitung') };
    }
  },

  // Demo-only (allowlist-gated → 404 elsewhere): resolve deterministic simulated system values for
  // the ticket's empty scaffold slots. Read-only on the server; the FE draft applies + persists them
  // (single persist path). Returns per-slot {slotId,value} for useKlaerfallDraft.fillSystemSlots.
  simulateSystemData: async (uuid: string): Promise<{ success: boolean; filledSlots?: { slotId: string; value: string }[]; error?: string }> => {
    try {
      const res = await coreHttp.post(`/tickets/${uuid}/simulate-system-data`, {});
      return { success: true, filledSlots: res.data?.filledSlots ?? [] };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Simulierte Systemdaten') };
    }
  },

  requestReanalysis: async (uuid: string, body: ReanalysisRequest, options?: { signal?: AbortSignal }): Promise<{ success: boolean; error?: string }> => {
    try {
      // Phase 4B expand window: dual-send both the old and new axis-vocabulary body key with the
      // SAME value — an old (pre-expansion) BE reads suggestedCategory/forceCategory, an expanded
      // one can assert the two agree. Canonical source is the new-named field (ReanalysisRequest);
      // the old key is computed FROM it, never the other way — same direction as the query-param senders above.
      const wireBody: ReanalysisRequest & Record<string, unknown> = { ...body };
      if (body.suggestedPrimaryAxis !== undefined) wireBody.suggestedCategory = body.suggestedPrimaryAxis;
      if (body.forcePrimaryAxis !== undefined) wireBody.forceCategory = body.forcePrimaryAxis;
      await coreHttp.post(`/tickets/${uuid}/request-reanalysis`, wireBody, { signal: options?.signal });
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Ablehnen') };
    }
  },

  unblockReanalysis: async (uuid: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await coreHttp.post(`/tickets/${uuid}/unblock-reanalysis`);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Entsperren') };
    }
  },

  archiveTicket: async (uuid: string): Promise<{ success: boolean; previousStatus?: string; error?: string }> => {
    try {
      const res = await coreHttp.post(`/tickets/${uuid}/archive`);
      return { success: true, previousStatus: res.data?.previousStatus };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Archivieren') };
    }
  },

  unarchiveTicket: async (uuid: string, restoreStatus?: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await coreHttp.post(`/tickets/${uuid}/unarchive`, restoreStatus ? { restoreStatus } : {});
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Wiederherstellen') };
    }
  },

  // Auto-button bulk lifecycle (Plan #2). BE returns the count actually changed —
  // already-archived rows in the input set are silent no-ops on BulkArchive.
  // Optional AbortSignal: aborting after the HTTP request reaches BE may still let
  // the server complete the transaction — the FE just discards the response and the
  // 30s RueckgaengigToast continues to provide undo. No client-state corruption.
  // Why a sibling of bulkArchive rather than a new client: it goes through coreHttp, so it inherits the
  // request interceptor's Bearer token with no auth code of its own. A call site that carried its own
  // auth would be one the next token change could silently miss.
  // responseType 'blob' is load-bearing — without it axios coerces the workbook bytes into a JS string
  // and the downloaded file is corrupt in a way that only shows when Excel opens it.
  // Two gestures, one endpoint. `uuids` = "these, which I picked". `selection` = "everything in the
  // view I am looking at" — the server resolves that from the bucket and filters, because the client
  // can only ever enumerate the page it happens to hold.
  downloadFormRowsXlsx: async (
    target:
      | { uuids: string[] }
      | { selection: { bucket: string; sort?: string; dir?: string; search?: string;
                       primaryAxis?: string; secondaryAxis?: string[];
                       from?: string | null; to?: string | null; uploadedBy?: string } },
    options?: { signal?: AbortSignal }
  ): Promise<{ blob?: Blob; includedUuids: string[]; matched: number; delivered: number; fileName?: string; error?: string }> => {
    try {
      // Phase 4B expand window: the SELECTION gesture's filter fields dual-send both the old and
      // new axis-vocabulary key with the SAME value, same as the query-param senders above. The
      // uuids gesture carries no axis filter and needs no transform.
      const wireTarget = 'selection' in target
        ? {
            selection: {
              ...target.selection,
              ...(target.selection.primaryAxis !== undefined ? { category: target.selection.primaryAxis } : {}),
              ...(target.selection.secondaryAxis !== undefined ? { reasonCodes: target.selection.secondaryAxis } : {}),
            },
          }
        : target;
      const res = await coreHttp.post('/tickets/form-rows-xlsx', wireTarget, {
        responseType: 'blob',
        signal: options?.signal,
      });
      // ⚠ MATCHED and DELIVERED are different numbers on purpose. The server selects on "an extraction
      // is stored"; the workbook additionally needs that extraction to be readable. A gap means rows
      // were selected and could not be rendered — the only detector the clerk has, and it also catches
      // the view having moved between the count they saw and the click they made.
      const matched = Number(res.headers['x-smarttix-rows-matched'] ?? 0);
      const delivered = Number(res.headers['x-smarttix-rows-delivered'] ?? 0);
      // Only the picked-uuid gesture names WHICH tickets survived; the selection gesture enumerated
      // nothing client-side, so there is no list to return and none is expected.
      const included = String(res.headers['x-smarttix-included'] || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      // The visible download name is rendered HERE because an anchor's download attribute always wins
      // over Content-Disposition for a blob URL. The server composes the same convention for callers
      // that hit the endpoint directly. Named from DELIVERED rows — the selection gesture has no
      // includedUuids to count.
      // Prefer the delivered count; fall back to the included list when that header is absent. The
      // fallback is not cosmetic: the selection gesture has no included list, and the uuid gesture
      // still names the file correctly against a backend that predates these headers — or when a
      // proxy strips them. Naming a workbook "0-Zeilen" while it holds rows is the same
      // authoritative-but-wrong artifact this whole change removes.
      return { blob: res.data as Blob, includedUuids: included, matched, delivered,
               fileName: notizblattFileName(delivered || included.length) };
    } catch (error: any) {
      return { includedUuids: [], matched: 0, delivered: 0, error: formatBackendError(error, 'Excel-Download') };
    }
  },

  bulkArchive: async (uuids: string[], options?: { signal?: AbortSignal }): Promise<{ archived: number; error?: string }> => {
    try {
      const res = await coreHttp.post<{ archived: number }>('/tickets/bulk-archive', { uuids }, { signal: options?.signal });
      return { archived: res.data.archived };
    } catch (error: any) {
      return { archived: 0, error: formatBackendError(error, 'Archivieren') };
    }
  },

  bulkUnarchive: async (uuids: string[], options?: { signal?: AbortSignal }): Promise<{ unarchived: number; error?: string }> => {
    try {
      const res = await coreHttp.post<{ unarchived: number }>('/tickets/bulk-unarchive', { uuids }, { signal: options?.signal });
      return { unarchived: res.data.unarchived };
    } catch (error: any) {
      return { unarchived: 0, error: formatBackendError(error, 'Wiederherstellen') };
    }
  },

  bulkProcess: async (uuids: string[], options?: { signal?: AbortSignal }): Promise<{ enqueued: number; skipped: number; error?: string }> => {
    try {
      const res = await coreHttp.post<{ enqueued: number; skipped: number }>('/tickets/bulk-process', { uuids }, { signal: options?.signal });
      return { enqueued: res.data.enqueued, skipped: res.data.skipped };
    } catch (error: any) {
      return { enqueued: 0, skipped: 0, error: formatBackendError(error, 'Verarbeiten') };
    }
  },

  // Liegengeblieben → Erledigt shortcut (Plan #2). Keeps status='archived' but flips
  // previous_status='suggestion_ready' → 'approved' so the row moves to the Erledigt bucket.
  bulkMarkErledigt: async (uuids: string[], options?: { signal?: AbortSignal }): Promise<{ markedErledigt: number; error?: string }> => {
    try {
      const res = await coreHttp.post<{ markedErledigt: number }>('/tickets/bulk-mark-erledigt', { uuids }, { signal: options?.signal });
      return { markedErledigt: res.data.markedErledigt };
    } catch (error: any) {
      return { markedErledigt: 0, error: formatBackendError(error, 'Markieren') };
    }
  },

  forceOcr: async (uuid: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await coreHttp.post(`/tickets/${uuid}/force-ocr`);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'OCR-Erzwingen') };
    }
  },

  downloadDraft: async (uuid: string): Promise<void> => {
    const response = await coreHttp.get(`/tickets/${uuid}/download-draft`, {
      responseType: 'blob',
    });
    downloadBlob(new Blob([response.data]), `response-${uuid}.eml`);
  },

  // PII ignore list — UI-only suppression of detected PII entries
  getPiiIgnoreList: async (): Promise<PiiIgnoreEntry[]> => {
    try {
      const response = await coreHttp.get<any[]>(`/pii-ignore`);
      return response.data.map((r: any) => ({
        id: r.id ?? r.Id,
        fieldType: r.fieldType ?? r.FieldType ?? r.field_type,
        value: r.value ?? r.Value,
      }));
    } catch {
      return [];
    }
  },

  addPiiIgnore: async (fieldType: string, value: string): Promise<{ id: number } | null> => {
    try {
      const response = await coreHttp.post('/pii-ignore', { fieldType, value });
      return { id: response.data?.id ?? response.data?.Id };
    } catch {
      return null;
    }
  },

  removePiiIgnore: async (id: number): Promise<boolean> => {
    try {
      await coreHttp.delete(`/pii-ignore/${id}`);
      return true;
    } catch {
      return false;
    }
  },

  getTicketReads: async (): Promise<{ readUuids: string[] }> => {
    const response = await coreHttp.get<{ readUuids: string[] }>(`/tickets/reads`);
    return response.data;
  },

  markTicketRead: async (uuid: string): Promise<void> => {
    await coreHttp.post(`/tickets/read`, { uuid });
  },
};

// ─── Admin API ──────────────────────────────────────────

export const listUsers = async (): Promise<UserEntry[]> => {
  const res = await coreHttp.get<UserEntry[]>(`/manage/users`);
  return res.data;
};

export const addUser = async (email: string, isNormalUser: boolean, isCustomerAdmin: boolean): Promise<void> => {
  await coreHttp.post('/manage/users', { email, isNormalUser, isCustomerAdmin });
};

export const deleteUser = async (id: number): Promise<void> => {
  await coreHttp.delete(`/manage/users/${id}`);
};

export const updateUserRoles = async (id: number, isNormalUser: boolean, isCustomerAdmin: boolean): Promise<void> => {
  await coreHttp.put(`/manage/users/${id}/roles`, { isNormalUser, isCustomerAdmin });
};

export const getCustomerConfig = async (): Promise<Record<string, string>> => {
  const res = await coreHttp.get<Record<string, string>>(`/manage/config`);
  return res.data;
};

export const updateCustomerConfig = async (entries: Record<string, string>): Promise<void> => {
  await coreHttp.put(`/manage/config`, { entries });
};

// D3+ answer-capability catalog — read-only mirror of the vnb_acts-backed capability registry
// (Task 11 CRUD API; CRUD surface itself is a later increment, this repo's Antwort-Katalog tab
// is view-only). stance/writeOwnership are closed vocabularies from smarttix.core; typed here
// verbatim so an unrecognized future value still round-trips instead of erroring.
export interface AnswerCapabilityEntry {
  id: string;
  displayName: string;
  stance: 'vorlage' | 'grounded_rag' | 'llm_wissen';
  writeOwnership: 'code' | 'tenant';
  triggers: string[];
  active: boolean;
}

export const listAnswerCatalog = async (): Promise<AnswerCapabilityEntry[]> => {
  const res = await coreHttp.get<AnswerCapabilityEntry[]>(`/manage/answer-catalog`);
  return res.data;
};

export interface ReplyPromptEntry {
  component: string;
  label: string;
  caution: boolean;
  effectiveText: string;
  source: 'override' | 'seed';
  seedText: string | null;
  activeRevision: number | null;
  latestRevision: number;
  // Frozen (BE-side, e.g. the two Notfall components): read-only, no override/rollback —
  // wording is compliance-reviewed and locked. Optional: older BE builds don't send it yet,
  // so absent MUST be treated as false (never assume frozen).
  frozen?: boolean;
}

export const listReplyPrompts = async (): Promise<ReplyPromptEntry[]> => {
  const res = await coreHttp.get<ReplyPromptEntry[]>(`/manage/reply-prompts`);
  return res.data;
};
export const saveReplyPrompt = async (component: string, text: string, baseRevision: number): Promise<void> => {
  await coreHttp.post(`/manage/reply-prompts`, { component, text, baseRevision });
};
export const rollbackReplyPrompt = async (component: string, revision: number): Promise<void> => {
  await coreHttp.post(`/manage/reply-prompts/${encodeURIComponent(component)}/rollback`, { revision });
};
export const resetReplyPrompt = async (component: string): Promise<void> => {
  await coreHttp.delete(`/manage/reply-prompts/${encodeURIComponent(component)}`);
};

// ── Vocabulary overlay rail (GET/POST /manage/vocabulary, DELETE /manage/vocabulary/{a}/{v}) ──
// Mirror of core VocabularyAdminFunctions' catalog records. The catalog is driven by the SEED
// vocabulary — one row per declared value, override or not — so this list is also the set of
// values an admin may edit; the rail can never add or remove one.
export interface VocabularyValueEntry {
  valueId: string;
  // The wording the tenant's seed declares, kept beside the effective one so the UI can say what
  // an edit is departing FROM without a second request.
  seedLabel: string;
  effectiveLabel: string;
  // Which layer produced effectiveLabel. 'seed' is a layer the override table cannot express
  // (absence IS seed), so the BE spells it out rather than making the FE re-derive precedence.
  source: 'seed' | 'institutional' | 'admin';
  // The value to send back as baseRevision. 0 = no admin row yet — a legitimate base, not a gap.
  adminRevision: number;
  // What a reset would REVEAL (institutional row if any, else seed) — so the confirmation can
  // name the wording that takes over instead of promising an unspecified "Standard".
  revertsTo: string;
}
export interface VocabularyAxisEntry {
  axis: string;
  noun: string;
  values: VocabularyValueEntry[];
}
export interface VocabularyCatalogResponse {
  role: string | null;
  axes: VocabularyAxisEntry[];
}

export const listVocabulary = async (): Promise<VocabularyCatalogResponse> => {
  const res = await coreHttp.get<VocabularyCatalogResponse>(`/manage/vocabulary`);
  return res.data;
};
// 409 (stale baseRevision) propagates to the caller exactly as saveReplyPrompt's does — the
// lost-update recovery is a reload, and only the editor knows how to preserve the rejected draft.
export const saveVocabularyLabel = async (
  axis: string, valueId: string, labelDe: string, baseRevision: number,
): Promise<void> => {
  await coreHttp.post(`/manage/vocabulary`, { axis, valueId, labelDe, baseRevision });
};
export const resetVocabularyLabel = async (axis: string, valueId: string): Promise<void> => {
  await coreHttp.delete(`/manage/vocabulary/${encodeURIComponent(axis)}/${encodeURIComponent(valueId)}`);
};

export interface VnbActUpsert {
  actId: string; name: string; beschreibung: string;
  triggerBeispiele: string[]; ausschlussBeispiele: string[];
  scope: string | null; prioritaet: number;
  stance: 'llm_wissen' | 'vorlage' | 'grounded_rag';
  vorlageText: string | null; aktiv: boolean;
}
export const upsertAct = async (act: VnbActUpsert): Promise<void> => {
  await coreHttp.post(`/manage/acts`, act);
};
export const deleteAct = async (actId: string): Promise<void> => {
  await coreHttp.delete(`/manage/acts/${encodeURIComponent(actId)}`);
};

// Full per-tenant act row — camelCase mirror of core's VnbAct record (GET /manage/acts,
// ActAdminFunctions, authed non-admin read). Unlike AnswerCapabilityEntry (the read-only catalog
// DTO above), this carries every field a tenant act has, so the edit form can seed itself
// completely instead of the lossy partial seed the catalog DTO forces.
export interface VnbAct {
  id: number;
  actId: string;
  name: string;
  beschreibung: string;
  triggerBeispiele: string[];
  ausschlussBeispiele: string[];
  scope: string | null;
  prioritaet: number;
  stance: 'llm_wissen' | 'vorlage' | 'grounded_rag';
  vorlageText: string | null;
  aktiv: boolean;
  updatedAt: string | null;
}
export const listActs = async (): Promise<VnbAct[]> => {
  const res = await coreHttp.get<VnbAct[]>(`/manage/acts`);
  return res.data;
};

// ─── Routing API (routing_rules, RoutingAdminFunctions) ────────────────────────────────
// A routing rule classifies an inbound mail into a named business case and tells the clerk how to
// proceed (internalText → "Routing: an X weiterleiten"). answerDirective (optional free text) is
// handed to the answer LLM to steer/force the customer reply ("Zwischeninfo an Kunden").
export interface RoutingRule {
  id: number;
  ruleId: string;
  name: string;
  triggerBeschreibung: string;
  triggerBeispiele: string[];
  internalText: string;
  erklaerung: string | null;
  answerDirective: string | null;
  targetUnit: string | null;
  prioritaet: number;
  aktiv: boolean;
  updatedAt: string | null;
}
export interface RoutingRuleUpsert {
  ruleId: string;
  name: string;
  triggerBeschreibung: string;
  triggerBeispiele: string[];
  internalText: string;
  erklaerung: string | null;
  answerDirective: string | null;
  targetUnit: string | null;
  prioritaet: number;
  aktiv: boolean;
}
export const listRoutingRules = async (): Promise<RoutingRule[]> => {
  const res = await coreHttp.get<RoutingRule[]>(`/manage/routing`);
  return res.data;
};
export const upsertRoutingRule = async (rule: RoutingRuleUpsert): Promise<void> => {
  await coreHttp.post(`/manage/routing`, rule);
};
export const deleteRoutingRule = async (ruleId: string): Promise<void> => {
  await coreHttp.delete(`/manage/routing/${encodeURIComponent(ruleId)}`);
};

// ─── Test Generator API ────────────────────────────────

export interface GenerateTestEmailRequest {
  role: string;
  edifact: boolean;
  targetCategory: string;
  noiseCategory?: string;
  strength: string;
  scenario?: string;
  topicCount?: number;       // 1 = Hauptthema only, 2 = + Nebenthema, 3 = + Drittes Thema
  mixedStrength?: string;    // strength for noiseCategory atom; ignored when noiseCategory unset
}

export interface GeneratedEmail {
  subject: string;
  body: string;
  emlBase64: string;
  expectedCategory: string;
  signalsUsed: string[];
}

export interface SignalAtomEntry {
  id: number;
  role: string;
  utility: string;
  channel: string;
  category: string;
  reasonCode: string;
  strength: string;
  atomText: string;
  isActive: boolean;
}

export const generateTestEmail = async (req: GenerateTestEmailRequest): Promise<GeneratedEmail> => {
  const res = await coreHttp.post<GeneratedEmail>('/tools/generate-test-email', req);
  return res.data;
};

// role is required — no default. A defaulted role here was the same silent-gmsb shape as the
// App.tsx/getConfig sites; the caller (TestgeneratorSeite) already holds the live confirmed role
// and must pass it explicitly.
export const listSignalAtoms = async (role: string): Promise<SignalAtomEntry[]> => {
  const res = await coreHttp.get<SignalAtomEntry[]>(`/tools/signal-atoms?role=${encodeURIComponent(role)}`);
  return res.data;
};

// Why: atoms are read-only for admin users. Managed via seed_signal_atoms.py → template → --db-only sync.

const emlSafeName = (subject: string): string =>
  subject.replace(/[^a-zA-Z0-9äöüÄÖÜß _-]/g, '').slice(0, 50) || 'test-email';

export const emlBase64ToFile = (emlBase64: string, subject: string): File => {
  const bytes = Uint8Array.from(atob(emlBase64), c => c.charCodeAt(0));
  return new File([bytes], `${emlSafeName(subject)}.eml`, { type: 'message/rfc822' });
};

export const downloadTestEmail = (emlBase64: string, subject: string): void => {
  const bytes = Uint8Array.from(atob(emlBase64), c => c.charCodeAt(0));
  const blob = new Blob([bytes], { type: 'message/rfc822' });
  downloadBlob(blob, `${emlSafeName(subject)}.eml`);
};
